import { Link } from "react-router-dom";
import { Twitter, Facebook, Linkedin, Activity } from "lucide-react";

// Pinterest icon component since lucide-react doesn't have it
const Pinterest = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { name: "Twitter", icon: Twitter, href: "https://twitter.com/datalevo1080" },
    { name: "Facebook", icon: Facebook, href: "https://web.facebook.com/profile.php?id=61577878760439" },
    { name: "LinkedIn", icon: Linkedin, href: "https://www.linkedin.com/in/shafi-tareen-507857268" },
    { name: "Pinterest", icon: Pinterest, href: "https://pin.it/5JywbwYD1" },
  ];

  const quickLinks = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/about" },
    { name: "Contact Us", href: "/contact" },
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Terms & Conditions", href: "/terms-and-conditions" },
  ];

  const toolLinks = [
    { name: "Walking Weight Loss Calculator", href: "/#calculator" },
  ];

  const legalLinks = [
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Disclaimer", href: "/disclaimer" },
    { name: "Terms & Conditions", href: "/terms-and-conditions" },
  ];

  return (
    <footer className="bg-[hsl(215,28%,12%)] text-[hsl(210,40%,98%)] border-t border-[hsl(215,25%,20%)]">
      <div className="container mx-auto px-4 py-10 md:py-14">
        {/* 4-Column Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {/* Column 1: Brand & Social */}
          <div className="space-y-4 sm:col-span-2 lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2 group">
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary shadow-md">
                <Activity className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="flex flex-col leading-none">
                <span
                  className="font-bold text-base text-white"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Walking
                  <span className="text-primary ml-1">Weight Loss</span>
                </span>
                <span className="text-[10px] text-[hsl(215,20%,65%)] font-medium tracking-wide uppercase">
                  Calculator
                </span>
              </div>
            </Link>
            <p className="text-sm text-[hsl(215,20%,65%)] leading-relaxed max-w-xs">
              Privacy-first, exportable tools by Datalevo.
            </p>
            <div className="flex items-center gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow us on ${social.name}`}
                  className="w-11 h-11 flex items-center justify-center rounded-lg bg-[hsl(215,25%,18%)] text-[hsl(215,20%,65%)] transition-all duration-300 hover:bg-primary hover:text-white hover:scale-110 hover:shadow-lg hover:shadow-primary/30 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-[hsl(215,28%,12%)]"
                >
                  <social.icon className="h-5 w-5 transition-transform duration-300 group-hover:rotate-6" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider">Quick Links</h3>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-[hsl(215,20%,65%)] hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Tools */}
          <div className="space-y-4">
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider">Our Tools</h3>
            <ul className="space-y-2.5">
              {toolLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-[hsl(215,20%,65%)] hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Legal & Support */}
          <div className="space-y-4">
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider">Support</h3>
            <ul className="space-y-2.5">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-[hsl(215,20%,65%)] hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="mailto:contact@datalevo.com"
                  className="text-sm text-[hsl(215,20%,65%)] hover:text-white transition-colors duration-200"
                >
                  contact@datalevo.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 pt-6 border-t border-[hsl(215,25%,20%)]">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[hsl(215,20%,55%)]">
              © {currentYear} Datalevo. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-xs">
              <Link to="/privacy-policy" className="text-primary hover:text-white transition-colors">
                Privacy
              </Link>
              <span className="text-[hsl(215,25%,30%)]">|</span>
              <Link to="/disclaimer" className="text-primary hover:text-white transition-colors">
                Disclaimer
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
